/* eslint-disable class-methods-use-this */
import { CancelToken } from 'axios';
import { CreateOrder, Order, OrderChange } from '../serviceOrder';
import { EntityDto } from '../dto/entityDto';
import { PagedResultDto } from '../dto/pagedResultDto';
import http from '../httpService';
import {
  CreateMasterBuild,
  MasterBuild,
  PagedMasterBuildHistoryRequest,
  PagedMasterBuildOrderRequest,
  PagedMasterBuildRequest,
  UpdateMasterBuild,
} from './dto/masterBuild';

class MasterBuildService {
  public async getAll(
    request: PagedMasterBuildRequest,
    cancelToken?: CancelToken
  ): Promise<PagedResultDto<MasterBuild>> {
    const result = await http.get('api/services/app/MasterBuild/GetAll', {
      params: request,
      cancelToken,
    });
    return result.data.result;
  }

  public async get(entityDto: EntityDto<string>): Promise<MasterBuild> {
    const result = await http.get('api/services/app/MasterBuild/Get', { params: entityDto });
    return result.data.result;
  }

  /** One page of a build's jobs. The build itself no longer carries them. */
  public async getOrders(
    request: PagedMasterBuildOrderRequest,
    cancelToken?: CancelToken
  ): Promise<PagedResultDto<Order>> {
    const result = await http.get('api/services/app/MasterBuild/GetOrders', {
      params: request,
      cancelToken,
    });
    return result.data.result;
  }

  /** One page of the build's unified history log - every job's changes, ordered by when they happened. */
  public async getHistory(
    request: PagedMasterBuildHistoryRequest,
    cancelToken?: CancelToken
  ): Promise<PagedResultDto<OrderChange>> {
    const result = await http.get('api/services/app/MasterBuild/GetHistory', {
      params: request,
      cancelToken,
    });
    return result.data.result;
  }

  public async create(build: CreateMasterBuild): Promise<MasterBuild> {
    const result = await http.post('api/services/app/MasterBuild/Create', build);
    return result.data.result;
  }  public async update(build: UpdateMasterBuild): Promise<MasterBuild> {
    const result = await http.put('api/services/app/MasterBuild/Update', build);
    return result.data.result;
  }

  public async delete(entityDto: EntityDto<string>): Promise<void> {
    await http.delete('api/services/app/MasterBuild/Delete', { params: entityDto });
  }

  /**
   * The three below are POST rather than PUT: ABP's dynamic API picks the verb from the method-name
   * prefix, and anything that is not Get/Create/Update/Delete defaults to POST.
   */
  public async addOrder(masterBuildId: string, order: CreateOrder): Promise<MasterBuild> {
    const result = await http.post('api/services/app/MasterBuild/AddOrder', {
      masterBuildId,
      order,
    });
    return result.data.result;
  }

  /**
   * Deletes one leg of the build - it only exists as part of the build. Soft-deleted.
   * DELETE with query params, not POST: ABP maps a "Delete" method prefix to the DELETE verb.
   */
  public async deleteOrder(masterBuildId: string, orderId: string): Promise<MasterBuild> {
    const result = await http.delete('api/services/app/MasterBuild/DeleteOrder', {
      params: { masterBuildId, orderId },
    });
    return result.data.result;
  }
}

export default new MasterBuildService();
